// frontend/src/contexts/ActiveInstitutionContext.tsx
/**
 * ActiveInstitutionContext — the user's institution memberships + which one
 * is currently active. Drives the school switcher in the header, the
 * institution-scoped cache keys (Phase E3), and the `X-Institution-Slug`
 * API header (Phase E2 via `setActiveSlugForApi`).
 *
 * Multi-membership data model (Phase B3 backend): a user can belong to
 * many institutions; exactly one is `is_active_for_user=TRUE` at a time.
 * Today (2026-05-16) every prod user has exactly one membership, so the
 * SchoolSwitcher renders a static badge — but the wiring is in place for
 * the dual-enrollment / transfer / advisor cases that motivated the
 * multi-membership refactor.
 *
 * Resolution at mount:
 *   1. localStorage cache (`active_institution:${supabaseUserId}`) — gives
 *      the next session an instant render of the previously-active school.
 *   2. Network fetch from GET /api/institutions/me — source of truth.
 *      Reconciles the cache against the backend's persisted active flag.
 *
 * Pre-auth state: provider stays in loading=false with empty memberships
 * and null active. Downstream consumers fall back to the DEFAULT slug
 * (single-domain model — the subdomain host fallback was removed with the
 * 2026-07-17 subdomain teardown).
 *
 * Phase E1 of multi-membership architecture
 * (docs/plans/multi-institution/11-multi-membership-architecture.md).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { apiClient, setActiveSlugForApi } from '../utils/apiClient';
import { makeInstitutionLocalStorage } from '../utils/institutionLocalStorage';
import { useAuth } from './AuthContext';

// =============================================================================
// TYPES
// =============================================================================

export interface MembershipInstitution {
  id: number;
  slug: string;
  name: string;
  abbreviation: string | null;
  country: string | null;
  timezone: string | null;
}

export interface Membership {
  institution: MembershipInstitution;
  role: 'student' | 'advisor' | 'admin';
  status: 'active' | 'transferred' | 'archived';
  isActiveForUser: boolean;
  joinedAt: string | null;
  major: string | null;
  enrollmentYear: number | null;
  enrollmentStartSemester: string | null;
  expectedGraduation: string | null;
  expectedGraduationSemester: string | null;
  completedCredits: number;
}

export interface ActiveInstitutionContextValue {
  memberships: Membership[];
  /** The active membership (whichever has `isActiveForUser=true`), or null
   *  if the user has zero memberships (mid-/sync state). */
  active: Membership | null;
  /** Convenience: `active.institution.id`. Null when no active. */
  activeId: number | null;
  /** Convenience: `active.institution.slug`. Null when no active. */
  activeSlug: string | null;
  /** Switch the active institution. Posts to /api/institutions/me/active,
   *  updates state + apiClient slug + localStorage. No-op if already active. */
  setActive: (institutionId: number) => Promise<void>;
  /** True while the initial /api/institutions/me fetch is in flight. */
  loading: boolean;
  /** Re-fetch /api/institutions/me. The provider fetches once per signed-in
   *  identity; AppContext calls this when a sync creates the account after
   *  that fetch saw no memberships (first sign-in, "Try again"). */
  refresh: () => Promise<void>;
}

// =============================================================================
// API SHAPES (manual until openapi.json regen picks up Phase B3 endpoints)
// =============================================================================

interface MembershipApiShape {
  institution: {
    id: number;
    slug: string;
    name: string;
    abbreviation: string | null;
    country: string | null;
    timezone: string | null;
  };
  role: 'student' | 'advisor' | 'admin';
  status: 'active' | 'transferred' | 'archived';
  is_active_for_user: boolean;
  joined_at: string | null;
  major: string | null;
  enrollment_year: number | null;
  enrollment_start_semester: string | null;
  expected_graduation: string | null;
  expected_graduation_semester: string | null;
  completed_credits: number;
}

interface MembershipsApiResponse {
  memberships: MembershipApiShape[];
  active_institution_id: number | null;
}

const adaptMembership = (m: MembershipApiShape): Membership => ({
  institution: m.institution,
  role: m.role,
  status: m.status,
  isActiveForUser: m.is_active_for_user,
  joinedAt: m.joined_at,
  major: m.major,
  enrollmentYear: m.enrollment_year,
  enrollmentStartSemester: m.enrollment_start_semester,
  expectedGraduation: m.expected_graduation,
  expectedGraduationSemester: m.expected_graduation_semester,
  completedCredits: m.completed_credits,
});

// =============================================================================
// CACHE
// =============================================================================

interface CachedActive {
  institutionId: number;
  slug: string;
  /** ISO timestamp; used only for diagnostics, not TTL — the backend is the
   *  source of truth and we reconcile on every mount. */
  cachedAt: string;
}

// M2 (Workstream M, 2026-05-19): cache get/set/clear scaffolding extracted
// to `utils/institutionLocalStorage` to share the pattern with
// InstitutionConfigContext. No TTL — backend is authoritative and we
// reconcile on every mount.
const activeCache = makeInstitutionLocalStorage<CachedActive>(
  'academic_planner_active_institution',
);

const loadFromCache = (supabaseUserId: string): CachedActive | null =>
  activeCache.load(supabaseUserId);

const saveToCache = (supabaseUserId: string, active: CachedActive): void =>
  activeCache.save(supabaseUserId, active);

const clearCache = (supabaseUserId: string): void =>
  activeCache.clear(supabaseUserId);

// =============================================================================
// CONTEXT
// =============================================================================

const DEFAULT_VALUE: ActiveInstitutionContextValue = {
  memberships: [],
  active: null,
  activeId: null,
  activeSlug: null,
  setActive: async () => {
    // No-op outside the provider (tests + pre-auth).
  },
  loading: false,
  refresh: async () => {
    // No-op outside the provider (tests + pre-auth).
  },
};

export const ActiveInstitutionContext =
  createContext<ActiveInstitutionContextValue>(DEFAULT_VALUE);

export const useActiveInstitution = (): ActiveInstitutionContextValue =>
  useContext(ActiveInstitutionContext);

/** Convenience hook for components that need the active membership's
 *  per-school academic state (major, enrollmentStartSemester, etc.).
 *  Returns null when the user has no active membership. */
export const useActiveMembership = (): Membership | null =>
  useActiveInstitution().active;

// =============================================================================
// PROVIDER
// =============================================================================

interface ProviderProps {
  children: ReactNode;
}

export const ActiveInstitutionProvider = ({ children }: ProviderProps) => {
  const { user } = useAuth();
  const supabaseUserId = user?.id ?? null;

  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Seed activeId from cache so first-paint matches the user's last session
  // without waiting for the network round-trip.
  useEffect(() => {
    if (!supabaseUserId) return;
    const cached = loadFromCache(supabaseUserId);
    if (cached) {
      setActiveId(cached.institutionId);
      setActiveSlugForApi(cached.slug);
    }
  }, [supabaseUserId]);

  const refresh = useCallback(async () => {
    if (!supabaseUserId) return;
    setLoading(true);
    try {
      // DELIBERATE raw-apiClient use (S5 F1): membership resolution is the
      // bootstrap that *sets* the active slug the typed client then sends as
      // X-Institution-Slug — it cannot itself go through typedApi.
      const body = await apiClient.get<MembershipsApiResponse>(
        '/api/institutions/me',
      );
      const adapted = (body.memberships ?? []).map(adaptMembership);
      setMemberships(adapted);
      const backendActive = body.active_institution_id;
      setActiveId(backendActive);
      const active = adapted.find(
        (m) => m.institution.id === backendActive,
      );
      if (active) {
        setActiveSlugForApi(active.institution.slug);
        saveToCache(supabaseUserId, {
          institutionId: active.institution.id,
          slug: active.institution.slug,
          cachedAt: new Date().toISOString(),
        });
      } else {
        // No active (zero memberships or backend-cleared) — drop cache
        // and let the apiClient interceptor fall back to the host slug.
        setActiveSlugForApi(null);
        clearCache(supabaseUserId);
      }
    } catch (error) {
      // Non-fatal: keep cached state, log once. The apiClient host fallback
      // continues to drive request routing.
      // eslint-disable-next-line no-console
      console.warn('ActiveInstitution fetch failed; keeping cached state', error);
    } finally {
      setLoading(false);
    }
  }, [supabaseUserId]);

  useEffect(() => {
    if (!supabaseUserId) {
      setMemberships([]);
      setActiveId(null);
      setActiveSlugForApi(null);
      return;
    }
    refresh();
  }, [supabaseUserId, refresh]);

  const setActive = useCallback(
    async (institutionId: number) => {
      if (!supabaseUserId) return;
      if (institutionId === activeId) return; // no-op on same-as-current
      const target = memberships.find(
        (m) => m.institution.id === institutionId,
      );
      if (!target) {
        // eslint-disable-next-line no-console
        console.warn(
          `ActiveInstitution.setActive: institution_id=${institutionId} not in memberships`,
        );
        return;
      }
      // Optimistic update — flip locally first, then persist.
      setActiveId(institutionId);
      setActiveSlugForApi(target.institution.slug);
      saveToCache(supabaseUserId, {
        institutionId: target.institution.id,
        slug: target.institution.slug,
        cachedAt: new Date().toISOString(),
      });
      try {
        const body = await apiClient.post<MembershipsApiResponse>(
          '/api/institutions/me/active',
          { institution_id: institutionId },
        );
        const adapted = (body.memberships ?? []).map(adaptMembership);
        setMemberships(adapted);
        setActiveId(body.active_institution_id);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('ActiveInstitution.setActive failed; reverting', error);
        // Re-fetch to recover the truth.
        refresh();
      }
    },
    [supabaseUserId, activeId, memberships, refresh],
  );

  const value: ActiveInstitutionContextValue = useMemo(() => {
    const active = memberships.find((m) => m.institution.id === activeId) ?? null;
    return {
      memberships,
      active,
      activeId,
      activeSlug: active ? active.institution.slug : null,
      setActive,
      loading,
      refresh,
    };
  }, [memberships, activeId, setActive, loading, refresh]);

  return (
    <ActiveInstitutionContext.Provider value={value}>
      {children}
    </ActiveInstitutionContext.Provider>
  );
};
